import { client } from "@/sdk";

export type ReportMoneyKey =
  | "stockNet"
  | "bondNet"
  | "estateNet"
  | "cashAmount"
  | "bankFee";
export type ReportSource =
  | "Confirmation"
  | "RequestSnapshot"
  | "UserBank"
  | "DraftInput"
  | "ExcelImport"
  | "NotApplicable"
  | "Missing";
export type ReportMoney = {
  value: string | null;
  source: ReportSource;
  locked: boolean;
};
export type ReportRowInput = {
  requestId: string;
  stockNet?: string | null;
  bondNet?: string | null;
  estateNet?: string | null;
  cashAmount?: string | null;
  bankFee?: string | null;
  bankName?: string | null;
  accountNo?: string | null;
  sourceNote?: string | null;
};
export type ReportDraftInput = {
  asOfDate: string;
  note?: string | null;
  rows: ReportRowInput[];
};
export type ReportRow = Record<ReportMoneyKey, ReportMoney> & {
  requestId: string;
  userId: string;
  displayName: string;
  email: string | null;
  requestStatus: string;
  stockCount: number;
  bondCount: number;
  estateCount: number;
  grossAmount: string | null;
  transferAmount: string | null;
  bankName: string | null;
  accountNo: string | null;
  bankNameLocked: boolean;
  accountNoLocked: boolean;
  sourceNote: string | null;
};
export type ReportIssue = {
  code: string;
  message: string;
  requestId?: string | null;
  field?: string | null;
  severity: "error" | "warning";
};
export type ReportGuidanceItem = {
  code: string;
  field: string | null;
  title: string;
  explanation: string;
  action: string;
  owner: string;
  evidenceRequired: string[];
  evidence: { label: string; reference: string }[];
  basis: "Generic" | "Audited";
  severity: "error" | "warning" | "info";
};
export type ReportRowGuidanceData = {
  requestId: string;
  items: ReportGuidanceItem[];
};
export type ReportDraft = {
  orderListId: string;
  version: number;
  state: "Draft" | "InReview" | "Approved";
  asOfDate: string;
  note: string | null;
  batchStatus: string;
  sourceHash: string;
  liveSourceHash: string;
  sourceChanged: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  approval: {
    actorId: string;
    at: string;
    note: string;
    fromVersion: number;
  } | null;
  importInfo:
    | (ReportImportPreview & {
        importedAt: string | null;
        importedBy: string | null;
      })
    | null;
  rows: ReportRow[];
  summary: Record<
    ReportMoneyKey | "grossAmount" | "transferAmount",
    string | null
  >;
  issues: ReportIssue[];
  rowGuidance: ReportRowGuidanceData[];
  batchGuidance: ReportGuidanceItem[];
  history: {
    action: string;
    version: number;
    actorId: string;
    at: string;
    message?: string | null;
  }[];
  input: ReportDraftInput;
};

export type ReportImportField = {
  field: string;
  label: string;
  excelValue: string | null;
  systemValue: string | null;
  draftValue: string | null;
  action: string;
};
export type ReportImportRow = {
  rowNumber: number;
  email: string | null;
  requestId: string | null;
  displayName: string;
  matchStatus: string;
  fields: ReportImportField[];
};
export type ReportImportPreview = {
  fileName: string;
  fileHash: string;
  templateId: string;
  previewHash: string;
  excelRowCount: number;
  requestCount: number;
  matchedCount: number;
  canApply: boolean;
  issues: ReportIssue[];
  rows: ReportImportRow[];
};

const importFields = `
  fileName fileHash templateId previewHash excelRowCount requestCount matchedCount canApply
  issues { code message requestId field severity }
  rows { rowNumber email requestId displayName matchStatus fields { field label excelValue systemValue draftValue action } }
`;

const fields = `
  orderListId version state asOfDate note batchStatus
  sourceHash liveSourceHash sourceChanged canSubmit canApprove
  approval { actorId at note fromVersion }
  importInfo { ${importFields} importedAt importedBy }
  rows {
    requestId userId displayName email requestStatus stockCount bondCount estateCount
    stockNet { value source locked } bondNet { value source locked }
    estateNet { value source locked } cashAmount { value source locked }
    bankFee { value source locked } grossAmount transferAmount
    bankName accountNo bankNameLocked accountNoLocked sourceNote
  }
  summary { stockNet bondNet estateNet cashAmount grossAmount bankFee transferAmount }
  issues { code message requestId field severity }
  rowGuidance { requestId items { code field title explanation action owner evidenceRequired evidence { label reference } basis severity } }
  batchGuidance { code field title explanation action owner evidenceRequired evidence { label reference } basis severity }
  history { action version actorId at message }
  input { asOfDate note rows { requestId stockNet bondNet estateNet cashAmount bankFee bankName accountNo sourceNote } }
`;

export async function getReportDraft(
  orderListId: string,
): Promise<ReportDraft> {
  const data = await client.request<{ withdrawalReportDraft: ReportDraft }>(
    `
    query WithdrawalReportDraft($orderListId: ID!) {
      withdrawalReportDraft(orderListId: $orderListId) { ${fields} }
    }
  `,
    { orderListId },
  );
  return data.withdrawalReportDraft;
}

type Revision = {
  orderListId: string;
  expectedVersion: number;
  sourceHash: string;
};

export type ReportFinalExcel = {
  fileName: string;
  mimeType: string;
  fileBase64: string;
  reportVersion: number;
  generatedAt: string;
  sha256: string;
};

export async function downloadReportFinalExcel(
  variables: Revision,
): Promise<ReportFinalExcel> {
  const data = await client.request<{
    downloadWithdrawalReportFinalExcel: ReportFinalExcel;
  }>(
    `
    mutation DownloadWithdrawalReportFinalExcel($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!) {
      downloadWithdrawalReportFinalExcel(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash) {
        fileName mimeType fileBase64 reportVersion generatedAt sha256
      }
    }
  `,
    variables,
  );
  return data.downloadWithdrawalReportFinalExcel;
}

export type ReportImportVariables = Revision & {
  fileName: string;
  fileBase64: string;
};

export async function previewReportImport(
  variables: ReportImportVariables,
): Promise<ReportImportPreview> {
  const data = await client.request<{
    previewWithdrawalReportImport: ReportImportPreview;
  }>(
    `
    mutation PreviewWithdrawalReportImport($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!, $fileName: String!, $fileBase64: String!) {
      previewWithdrawalReportImport(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash, fileName: $fileName, fileBase64: $fileBase64) { ${importFields} }
    }
  `,
    variables,
  );
  return data.previewWithdrawalReportImport;
}

export async function applyReportImport(
  variables: ReportImportVariables & { previewHash: string },
): Promise<ReportDraft> {
  const data = await client.request<{
    applyWithdrawalReportImport: ReportDraft;
  }>(
    `
    mutation ApplyWithdrawalReportImport($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!, $fileName: String!, $fileBase64: String!, $previewHash: String!) {
      applyWithdrawalReportImport(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash, fileName: $fileName, fileBase64: $fileBase64, previewHash: $previewHash) { ${fields} }
    }
  `,
    variables,
  );
  return data.applyWithdrawalReportImport;
}

export async function approveReportDraft(
  variables: Revision & { reviewNote: string },
): Promise<ReportDraft> {
  const data = await client.request<{
    approveWithdrawalReportDraft: ReportDraft;
  }>(
    `
    mutation ApproveWithdrawalReportDraft($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!, $reviewNote: String!) {
      approveWithdrawalReportDraft(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash, reviewNote: $reviewNote) { ${fields} }
    }
  `,
    variables,
  );
  return data.approveWithdrawalReportDraft;
}

export async function returnReportDraft(
  variables: Revision & { reason: string },
): Promise<ReportDraft> {
  const data = await client.request<{
    returnWithdrawalReportDraft: ReportDraft;
  }>(
    `
    mutation ReturnWithdrawalReportDraft($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!, $reason: String!) {
      returnWithdrawalReportDraft(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash, reason: $reason) { ${fields} }
    }
  `,
    variables,
  );
  return data.returnWithdrawalReportDraft;
}

export async function saveReportDraft(
  variables: Revision & { input: ReportDraftInput },
): Promise<ReportDraft> {
  const data = await client.request<{ saveWithdrawalReportDraft: ReportDraft }>(
    `
    mutation SaveWithdrawalReportDraft($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!, $input: WithdrawalReportDraftInput!) {
      saveWithdrawalReportDraft(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash, input: $input) { ${fields} }
    }
  `,
    variables,
  );
  return data.saveWithdrawalReportDraft;
}

export async function refreshReportDraft(
  variables: Revision,
): Promise<ReportDraft> {
  const data = await client.request<{
    refreshWithdrawalReportDraft: ReportDraft;
  }>(
    `
    mutation RefreshWithdrawalReportDraft($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!) {
      refreshWithdrawalReportDraft(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash) { ${fields} }
    }
  `,
    variables,
  );
  return data.refreshWithdrawalReportDraft;
}

export async function submitReportDraft(
  variables: Revision,
): Promise<ReportDraft> {
  const data = await client.request<{
    submitWithdrawalReportDraft: ReportDraft;
  }>(
    `
    mutation SubmitWithdrawalReportDraft($orderListId: ID!, $expectedVersion: Int!, $sourceHash: String!) {
      submitWithdrawalReportDraft(orderListId: $orderListId, expectedVersion: $expectedVersion, sourceHash: $sourceHash) { ${fields} }
    }
  `,
    variables,
  );
  return data.submitWithdrawalReportDraft;
}
